import { Box, Button, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { synchronizationAction } from 'actions';
import { updateCurrentConnection } from 'actions/synchronization';
import { Select } from 'chakra-react-select';
import { EmptyState, PageContainer, PageHeader, Skeleton } from 'components/ds';
import { RootState } from 'helpers/store';
import { useAppDispatch, useAppSelector } from 'hooks';
import { CornerDownLeft, FolderGit2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import SyncTable from './SyncTable';

type Projects = {
  name: string;
  display_name: string;
};

const ProjectList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { connections, projects, isLoading, current } = useAppSelector(
    (state: RootState) => state.synchronization
  );

  useEffect(() => {
    if (current.connection_id) {
      dispatch(synchronizationAction.fetchProjectList({ connection_id: current.connection_id }));
    }
  }, [current.connection_id, dispatch]);

  useEffect(() => {
    if (connections?.list?.length && !current.connection_id) {
      dispatch(updateCurrentConnection(connections.list[0].id));
    }
  }, [dispatch, current.connection_id, connections]);

  useEffect(() => {
    dispatch(synchronizationAction.fetchConnectionList());
    return () => {
      dispatch(synchronizationAction.resetSyncProject());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(synchronizationAction.resetProject());
  }, []);

  const columns = useMemo<ColumnDef<Projects>[]>(
    () => [
      {
        id: 'index',
        accessorKey: 'index',
        header: t('syncProject.no.'),
        cell: (info) => {
          return info.row.index + 1;
        },
        size: 50,
      },
      {
        accessorKey: 'display_name',
        header: t('syncProject.projectName'),
        cell: (info) => (
          <Text
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
              color: 'primary.solid',
              fontWeight: 500,
              _hover: { textDecoration: 'underline' },
            }}
            onClick={(e) => handleOpenProject(e, info.row.original.name)}
          >
            {info.getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: 'name',
        header: t('syncProject.url'),
        cell: (info) => (
          <Tooltip label={info.getValue() as string}>
            <Text
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'text.muted',
              }}
            >
              {info.getValue() as string}
            </Text>
          </Tooltip>
        ),
      },
    ],
    [t]
  );

  const handleOpenProject = (
    e: React.MouseEvent<HTMLParagraphElement, MouseEvent>,
    projectName: string
  ) => {
    e.preventDefault();
    dispatch(synchronizationAction.updateCurrentProject(projectName));
    dispatch(synchronizationAction.fetchDocumentListByProject(''));
    history.push('/sync-documents');
  };

  const handleSelectChange = (item: any) => {
    if (item.value) {
      dispatch(synchronizationAction.updateCurrentConnection(item.value));
    }
  };

  const selectedOption = useMemo(() => {
    const selected = connections.list?.find((item) => item.id === current?.connection_id);
    return selected
      ? {
          value: selected.id,
          label: selected.display_name,
        }
      : {};
  }, [connections, current.connection_id]);

  return (
    <PageContainer>
      {/* Page header — DS PageHeader (mockup .page-header), back-to-list action right */}
      <PageHeader
        title={t('syncProject.yourProject')}
        action={
          <Link to={'/synchronization'}>
            <Button leftIcon={<CornerDownLeft size={16} />} size='ds-md' variant='ds-outline'>
              {t('syncProject.viewList')}
            </Button>
          </Link>
        }
      />

      <VStack alignItems={'flex-start'} spacing={3}>
        {/* Toolbar — connection select (mockup .toolbar) */}
        <HStack sx={{ width: '100%' }} flexWrap='wrap'>
          <Box sx={{ width: '35%', minWidth: '240px' }}>
            <Select
              options={connections.select_options}
              onChange={handleSelectChange}
              value={selectedOption}
              chakraStyles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
              noOptionsMessage={() => {
                return t('common.noOptions');
              }}
            />
          </Box>
        </HStack>

        {/* Table wrapper — mockup .table-wrap (r-lg border surface) */}
        <Box
          sx={{ width: '100%' }}
          overflow='hidden'
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          borderRadius='lg'
        >
          {isLoading ? (
            /* DS loading skeleton — mirrors table rows */
            <Box p={4}>
              <Skeleton h='14px' w='40%' mb={4} />
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} h='44px' borderRadius='md' mb={2} />
              ))}
            </Box>
          ) : projects.list.length ? (
            <SyncTable columns={columns} data={projects.list} />
          ) : (
            /* DS empty state */
            <EmptyState
              icon={<FolderGit2 size={26} />}
              title={t('common.noData')}
              description={t('syncProject.emptyProjects', {
                defaultValue: 'No projects found for this connection.',
              })}
            />
          )}
        </Box>
      </VStack>
    </PageContainer>
  );
};

export default ProjectList;
